import { useMemo } from "react";

/**
 * Returns true when the page is running inside a cross-origin iframe.
 *
 * Uses the `window.top === window.self` identity check and catches
 * cross-origin `SecurityError` exceptions by treating them as framed.
 * The optional reader supplies the browser identity boundary for non-browser tests.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const framed = isFramedContext();
 * const identity = {};
 * isFramedContext(() => ({ top: identity, self: identity })); // false
 * ```
 */
export function isFramedContext(
  readWindow: () => {
    readonly top: object | null;
    readonly self: object;
  } = () => window,
): boolean {
  try {
    const context = readWindow();
    return context.top !== context.self;
  } catch {
    return true;
  }
}

/**
 * React hook that returns whether the current render context is inside a cross-origin iframe.
 *
 * @since 0.0.0
 * @example
 * ```tsx
 * const framed = useIsFramed()
 * ```
 */
export function useIsFramed(): boolean {
  return useMemo(() => isFramedContext(), []);
}
